import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { usePermissions } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';

// Sotuv bo'limi FAQAT 'good' (tovar), servis bo'limi FAQAT 'service'
// (xizmat) turidagi mahsulot yarata/tahrirlay oladi - backend/app/routers/
// products.py check_product_type_access bilan bir xil xarita (ombor/
// buxgalteriya/admin/direktor cheklanmaydi, shuning uchun xaritada yo'q).
const DEPARTMENT_ALLOWED_PRODUCT_TYPE: Record<string, 'good' | 'service'> = {
  sales: 'good',
  service: 'service',
};

interface Supplier { id: number; company_name: string; }
interface Category { id: number; name: string; }
interface StockMovement {
  id: number; movement_type: string; quantity: number;
  note?: string; created_at: string;
}
interface Product {
  id: number; name: string; name_ru?: string; name_en?: string; code: string; description?: string;
  unit: string; purchase_price: number; sale_price: number;
  min_stock: number; current_stock: number; manufacturer?: string;
  part_number?: string; model_number?: string; country_of_origin?: string;
  image_url?: string; category?: Category;
  supplier_id?: number; supplier?: Supplier;
  product_type?: string;
  instrument_name?: string;
  hs_code_manufacturer?: string;
  hs_code_uz?: string;
  mxik_code?: string;
}

const unitLabels: Record<string, string> = {
  piece: 'Dona', box: 'Quti', pack: 'Paket',
  liter: 'Litr', kg: 'Kg', meter: 'Metr',
  hour: 'Soat', service_unit: 'Xizmat',
};

const movementLabels: Record<string, { label: string; color: string }> = {
  income: { label: 'Kirim', color: 'text-green-600' },
  outcome: { label: 'Chiqim', color: 'text-red-600' },
  adjustment: { label: 'Tuzatish', color: 'text-blue-600' },
};

const emptyForm = {
  name: '', code: '', description: '', unit: 'piece',
  purchase_price: 0, sale_price: 0, min_stock: 0,
  current_stock: 0, manufacturer: '', category_id: '', supplier_id: '',
  product_type: 'good', instrument_name: '',
  name_ru: '', name_en: '',
  hs_code_manufacturer: '', hs_code_uz: '', mxik_code: '',
  part_number: '', model_number: '', country_of_origin: '',
};

const Products: React.FC = () => {
  const { canEdit } = usePermissions();
  const { user } = useAuth();
  const canFull = canEdit('products');
  // Kosmetik cheklov - haqiqiy huquq har doim backendda tekshiriladi
  // (check_product_type_access, products.py). Bo'lim aniqlanmagan bo'lsa
  // (ombor/buxgalteriya/admin/direktor) - hech qanday cheklov yo'q.
  const allowedProductType = user?.department ? DEPARTMENT_ALLOWED_PRODUCT_TYPE[user.department] : undefined;
  const rowEditable = (p: Product) => canFull && (!allowedProductType || (p.product_type || 'good') === allowedProductType);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showCatModal, setShowCatModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState<Product | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState<Product | null>(null);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [catForm, setCatForm] = useState({ name: '', description: '' });
  const [stockForm, setStockForm] = useState({ movement_type: 'income', quantity: 1, note: '' });
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterUnit, setFilterUnit] = useState('');
  const [filterLowStock, setFilterLowStock] = useState(false);
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [error, setError] = useState('');
  const [showImportInfo, setShowImportInfo] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  const handlePriceList = async (disc: number) => {
    try {
      const res = await api.get('/products/export/price-list', {
        params: { discount: disc, currency: 'UZS' },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `price_list${disc > 0 ? '_chegirma' + disc : ''}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      setShowPriceModal(false);
    } catch { alert('Price list yaratishda xato'); }
  };

  const fetchData = async () => {
    try {
      const p = await api.get('/products');
      setProducts(p.data);
    } catch (e) {
      console.error('Mahsulotlarni yuklashda xato:', e);
    }
    try {
      const c = await api.get('/categories');
      setCategories(c.data);
    } catch (e) {
      console.error('Kategoriyalarni yuklashda xato:', e);
    }
    try {
      const s = await api.get('/suppliers');
      setSuppliers(s.data);
    } catch (e) {
      setSuppliers([]);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.code.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat ? p.category?.id === Number(filterCat) : true;
    const matchUnit = filterUnit ? p.unit === filterUnit : true;
    const matchLow = filterLowStock ? p.current_stock <= p.min_stock : true;
    return matchSearch && matchCat && matchUnit && matchLow;
  });

  const openAdd = () => {
    setForm({ ...emptyForm, product_type: allowedProductType || emptyForm.product_type });
    setEditId(null); setError(''); setShowModal(true);
  };
  const openEdit = (p: Product) => {
    setForm({
      name: p.name, name_ru: p.name_ru || '', name_en: p.name_en || '', code: p.code,
      description: p.description || '', unit: p.unit,
      purchase_price: p.purchase_price, sale_price: p.sale_price,
      min_stock: p.min_stock, current_stock: p.current_stock,
      manufacturer: p.manufacturer || '', category_id: p.category?.id?.toString() || '',
      supplier_id: p.supplier?.id?.toString() || '',
      product_type: p.product_type || 'good',
      instrument_name: p.instrument_name || '',
      hs_code_manufacturer: p.hs_code_manufacturer || '', hs_code_uz: p.hs_code_uz || '', mxik_code: p.mxik_code || '',
      part_number: p.part_number || '', model_number: p.model_number || '', country_of_origin: p.country_of_origin || '',
    });
    setEditId(p.id); setError(''); setShowModal(true);
  };

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      const payload = {
        ...form,
        purchase_price: Number(form.purchase_price),
        sale_price: Number(form.sale_price),
        min_stock: form.product_type === 'service' ? 0 : Number(form.min_stock),
        current_stock: form.product_type === 'service' ? 0 : Number(form.current_stock),
        category_id: form.category_id ? Number(form.category_id) : null,
        supplier_id: form.supplier_id ? Number(form.supplier_id) : null,
        instrument_name: form.instrument_name || null,
      };
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post('/products', payload);
      setShowModal(false); fetchData();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Xatolik yuz berdi');
    } finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("O'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/products/${id}`); fetchData();
  };

  const handleAddCategory = async () => {
    try {
      await api.post('/categories', catForm);
      setCatForm({ name: '', description: '' });
      setShowCatModal(false); fetchData();
    } catch (e: any) { alert(e.response?.data?.detail || 'Xatolik'); }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm("Kategoriyani o'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/categories/${id}`); fetchData();
  };

  const handleStockMovement = async () => {
    if (!showStockModal) return;
    setLoading(true);
    try {
      await api.post(`/products/${showStockModal.id}/stock`, {
        ...stockForm, quantity: Number(stockForm.quantity),
      });
      setShowStockModal(null); fetchData();
    } catch (e: any) { alert(e.response?.data?.detail || 'Xatolik'); }
    finally { setLoading(false); }
  };

  const handleHistory = async (p: Product) => {
    const res = await api.get(`/products/${p.id}/stock`);
    setMovements(res.data); setShowHistoryModal(p);
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/products/export/excel', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = 'mahsulotlar.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { alert('Export xatoligi'); }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const formData = new FormData(); formData.append('file', file);
    try {
      const res = await api.post('/products/import/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(`✅ Qo'shildi: ${res.data.added} ta, O'tkazib yuborildi: ${res.data.skipped} ta`);
      fetchData();
    } catch { alert('Import xatoligi'); }
    e.target.value = '';
  };

  const handleImageUpload = async (productId: number, file: File) => {
    const formData = new FormData(); formData.append('file', file);
    await api.post(`/products/${productId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    fetchData();
  };

  const lowStockCount = products.filter(p => p.current_stock <= p.min_stock).length;

  return (
    <div className="p-3 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-2 mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Mahsulotlar</h2>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowPriceModal(true)}
            className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs md:text-sm">
            📄 <span className="hidden sm:inline">Narxlar ro'yxati</span><span className="sm:hidden">Narx</span>
          </button>
          <button onClick={() => canFull && setShowCatModal(true)} disabled={!canFull}
            className={`px-3 py-2 border border-gray-300 rounded-lg text-xs md:text-sm ${canFull ? 'hover:bg-gray-50' : 'opacity-40 cursor-not-allowed'}`}>
            📁 <span className="hidden sm:inline">Kategoriyalar</span><span className="sm:hidden">Kat.</span>
          </button>
          <button onClick={() => canFull && handleExport()} disabled={!canFull}
            className={`px-3 py-2 bg-green-600 text-white rounded-lg text-xs md:text-sm ${canFull ? 'hover:bg-green-700' : 'opacity-40 cursor-not-allowed'}`}>
            ⬇️ Excel
          </button>
          <button onClick={() => canFull && importRef.current?.click()} disabled={!canFull}
            className={`px-3 py-2 bg-orange-500 text-white rounded-lg text-xs md:text-sm ${canFull ? 'hover:bg-orange-600' : 'opacity-40 cursor-not-allowed'}`}>
            ⬆️ Import
          </button>
          <input ref={importRef} type="file" accept=".xlsx" onChange={handleImport} className="hidden" disabled={!canFull} />
          <button onClick={() => canFull && openAdd()} disabled={!canFull}
            className={`px-3 py-2 bg-blue-600 text-white rounded-lg text-xs md:text-sm ${canFull ? 'hover:bg-blue-700' : 'opacity-40 cursor-not-allowed'}`}>
            + <span className="hidden sm:inline">Qo'shish</span>
          </button>
        </div>
      </div>

      {/* Import shablon haqida ma'lumot */}
      {canFull && (
        <div className="mb-4">
          <button onClick={() => setShowImportInfo(!showImportInfo)}
            className="text-xs text-blue-600 hover:underline flex items-center gap-1">
            ℹ️ Import Excel ustunlari haqida
            <span className={`transition-transform inline-block ${showImportInfo ? 'rotate-180' : ''}`}>▾</span>
          </button>
          {showImportInfo && (
            <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
              <p className="font-semibold mb-1">📋 Excel import fayli ustunlari (tartib muhim):</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                {[
                  'A: Nomi (O\'zbek) *', 'B: Nomi (Русский)', 'C: Name (English)',
                  'D: Kodi *', 'E: Tavsif', 'F: Birlik (piece/box/pack/liter/kg/meter)',
                  'G: Sotib olish narxi', 'H: Sotish narxi', 'I: Min. qoldiq',
                  'J: Joriy qoldiq', 'K: Ishlab chiqaruvchi', 'L: Kategoriya',
                ].map(col => (
                  <span key={col} className="bg-white rounded px-2 py-0.5 border border-blue-200">{col}</span>
                ))}
              </div>
              <p className="mt-2 text-blue-600">💡 Birinchi qatorni sarlavha sifatida o'tkazib yuborish mumkin. * belgili ustunlar majburiy.</p>
            </div>
          )}
        </div>
      )}

      {lowStockCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">
          ⚠️ {lowStockCount} ta mahsulot minimal qoldiqdan kam!
        </div>
      )}

      {/* Filtrlar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4">
        <input type="text" placeholder="🔍 Qidirish..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="col-span-2 md:col-span-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Barcha kategoriyalar</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterUnit} onChange={e => setFilterUnit(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Barcha birliklar</option>
          {Object.entries(unitLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className="flex items-center gap-2 border rounded-lg px-3 py-2 text-sm cursor-pointer">
          <input type="checkbox" checked={filterLowStock} onChange={e => setFilterLowStock(e.target.checked)} />
          Kam qoldiqlar
        </label>
      </div>

      {/* Jadval */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-3 py-3 text-left">Mahsulot</th>
              <th className="px-3 py-3 text-left hidden md:table-cell">Kategoriya</th>
              <th className="px-3 py-3 text-left hidden md:table-cell">Birlik</th>
              <th className="px-3 py-3 text-right hidden lg:table-cell">Sotib olish</th>
              <th className="px-3 py-3 text-right">Sotish</th>
              <th className="px-3 py-3 text-center">Qoldiq</th>
              <th className="px-3 py-3 text-center">Amallar</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-gray-400">Mahsulotlar yo'q</td></tr>
            ) : filtered.map(p => (
              <tr key={p.id} className={`border-t hover:bg-gray-50 ${p.current_stock <= p.min_stock ? 'bg-red-50' : ''}`}>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2 md:gap-3">
                    {p.image_url ? (
                      <label className={`relative group flex-shrink-0 ${rowEditable(p) ? 'cursor-pointer' : 'cursor-default'}`}>
                        <img src={`https://erp.labin.uz/api${p.image_url}`} alt={p.name}
                          className={`w-8 h-8 md:w-10 md:h-10 rounded object-cover border transition ${rowEditable(p) ? 'group-hover:opacity-70' : ''}`} />
                        {rowEditable(p) && (
                          <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 text-xs text-white bg-black bg-opacity-40 rounded">✏️</span>
                        )}
                        <input type="file" accept="image/*" className="hidden" disabled={!rowEditable(p)}
                          onChange={e => rowEditable(p) && e.target.files && handleImageUpload(p.id, e.target.files[0])} />
                      </label>
                    ) : (
                      <label className={`flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs ${rowEditable(p) ? 'cursor-pointer hover:border-blue-400' : 'cursor-default opacity-50'}`}>
                        📷
                        <input type="file" accept="image/*" className="hidden" disabled={!rowEditable(p)}
                          onChange={e => rowEditable(p) && e.target.files && handleImageUpload(p.id, e.target.files[0])} />
                      </label>
                    )}
                    <div className="min-w-0 max-w-xs">
                      <div className="font-medium text-gray-800 flex items-center gap-1 flex-wrap">
                        <span className="break-words">{p.name}</span>
                        {p.product_type === 'service' && (
                          <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full whitespace-nowrap">Xizmat</span>
                        )}
                      </div>
                      {p.name_ru && <div className="text-xs text-gray-400 truncate">{p.name_ru}</div>}
                      <div className="text-xs text-blue-500 font-mono">{p.code}</div>
                      {p.part_number && <div className="text-xs text-gray-400 hidden sm:block">Kataloq: {p.part_number}</div>}
                      {p.manufacturer && <div className="text-xs text-gray-400 hidden sm:block">{p.manufacturer}</div>}
                      {(p.model_number || p.country_of_origin) && (
                        <div className="text-xs text-gray-400 hidden sm:block truncate">
                          {p.model_number && `Model: ${p.model_number}`}{p.model_number && p.country_of_origin && ' · '}{p.country_of_origin}
                        </div>
                      )}
                      {(p.hs_code_manufacturer || p.hs_code_uz) && (
                        <div className="text-xs text-gray-400 hidden md:block truncate">
                          🛃{p.hs_code_manufacturer && ` M: ${p.hs_code_manufacturer}`}{p.hs_code_uz && ` · UZ: ${p.hs_code_uz}`}
                        </div>
                      )}
                      {/* Mobile: kategoriya va birlik */}
                      <div className="md:hidden text-xs text-gray-400 mt-0.5">
                        {p.category?.name && <span>{p.category.name} · </span>}
                        <span>{unitLabels[p.unit]}</span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-500 hidden md:table-cell">{p.category?.name || '—'}</td>
                <td className="px-3 py-3 hidden md:table-cell">{unitLabels[p.unit]}</td>
                <td className="px-3 py-3 text-right hidden lg:table-cell">{p.purchase_price.toLocaleString()}</td>
                <td className="px-3 py-3 text-right text-green-600 font-medium whitespace-nowrap">
                  {p.sale_price.toLocaleString()}
                </td>
                <td className="px-3 py-3 text-center whitespace-nowrap">
                  <span className={`font-bold ${p.current_stock <= p.min_stock ? 'text-red-600' : 'text-gray-800'}`}>
                    {p.current_stock}
                  </span>
                  <span className="text-gray-400 text-xs hidden sm:inline"> / {p.min_stock}</span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => handleHistory(p)} title="Tarix"
                      className="p-1.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-xs">📋</button>
                    <button onClick={() => rowEditable(p) && setShowStockModal(p)} disabled={!rowEditable(p)}
                      title={rowEditable(p) ? "Kirim/Chiqim" : "Faqat ko'rish"}
                      className={`p-1.5 rounded text-xs ${rowEditable(p) ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>📦</button>
                    <button onClick={() => rowEditable(p) && openEdit(p)} disabled={!rowEditable(p)}
                      title={rowEditable(p) ? "Tahrirlash" : "Faqat ko'rish"}
                      className={`p-1.5 rounded text-xs ${rowEditable(p) ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>✏️</button>
                    <button onClick={() => rowEditable(p) && handleDelete(p.id)} disabled={!rowEditable(p)}
                      title={rowEditable(p) ? "O'chirish" : "Faqat ko'rish"}
                      className={`p-1.5 rounded text-xs ${rowEditable(p) ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mahsulot Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editId ? 'Tahrirlash' : 'Yangi mahsulot'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {/* Tur tanlash */}
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Mahsulot turi</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" value="good" checked={form.product_type === 'good'}
                      disabled={!!allowedProductType && allowedProductType !== 'good'}
                      onChange={e => setForm({ ...form, product_type: e.target.value })} />
                    <span className="text-sm">📦 Tovar</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" value="service" checked={form.product_type === 'service'}
                      disabled={!!allowedProductType && allowedProductType !== 'service'}
                      onChange={e => setForm({ ...form, product_type: e.target.value })} />
                    <span className="text-sm">🔧 Xizmat</span>
                  </label>
                </div>
              </div>
              {/* Xizmat uchun instrument nomi */}
              {form.product_type === 'service' && (
                <div className="col-span-1 md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instrument nomi</label>
                  <input type="text" value={form.instrument_name}
                    onChange={e => setForm({ ...form, instrument_name: e.target.value })}
                    placeholder="HPLC DAD, GC-MS, LC-MS/MS..."
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                </div>
              )}

              {/* Nomlar - 3 tilda */}
              <div className="col-span-1 md:col-span-2">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">🌐 Ko'p tilli nomlar</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">🇺🇿 O'zbek nomi *</label>
                    <input type="text" value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="Mahsulot nomi..."
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">🇷🇺 Русское название</label>
                    <input type="text" value={form.name_ru}
                      onChange={e => setForm({ ...form, name_ru: e.target.value })}
                      placeholder="Название на русском..."
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">🇬🇧 English Name</label>
                    <input type="text" value={form.name_en}
                      onChange={e => setForm({ ...form, name_en: e.target.value })}
                      placeholder="Product name in English..."
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                  </div>
                </div>
              </div>

              {/* Asosiy ma'lumotlar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kodi *</label>
                <input type="text" value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ishlab chiqaruvchi (Brend)</label>
                <input type="text" value={form.manufacturer}
                  onChange={e => setForm({ ...form, manufacturer: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>

              {/* Katalog raqami va model */}
              <div className="col-span-1 md:col-span-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Ishlab chiqaruvchi katalog raqami</label>
                    <input type="text" value={form.part_number}
                      onChange={e => setForm({ ...form, part_number: e.target.value })}
                      placeholder="Masalan: 285698"
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Model / Kod (ishlab chiqaruvchi)</label>
                    <input type="text" value={form.model_number}
                      onChange={e => setForm({ ...form, model_number: e.target.value })}
                      placeholder="Masalan: T-1"
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                </div>
              </div>
              <div className="col-span-1 md:col-span-2">
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">Ishlab chiqarilgan mamlakat</label>
                <input type="text" value={form.country_of_origin}
                  onChange={e => setForm({ ...form, country_of_origin: e.target.value })}
                  placeholder="Masalan: Germaniya"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
              </div>

              {/* HS kodlari */}
              <div className="col-span-1 md:col-span-2">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2 border-b pb-1">🛃 HS kodlari (bojxona)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Ishlab chiqaruvchi HS kodi</label>
                    <input type="text" value={form.hs_code_manufacturer}
                      onChange={e => setForm({ ...form, hs_code_manufacturer: e.target.value })}
                      placeholder="Masalan: 2933.99.00"
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">O'zbekiston HS kodi</label>
                    <input type="text" value={form.hs_code_uz}
                      onChange={e => setForm({ ...form, hs_code_uz: e.target.value })}
                      placeholder="Masalan: 2933990000"
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">MXIK (ИКПУ) kodi</label>
                    <input type="text" value={form.mxik_code}
                      onChange={e => setForm({ ...form, mxik_code: e.target.value })}
                      placeholder="17 xonali kod (ixtiyoriy)"
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm" />
                    <p className="text-[11px] text-gray-400 mt-1">Elektron hisob-faktura uchun. Hozircha hujjatlarda chiqmaydi.</p>
                  </div>
                </div>
              </div>

              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tavsif</label>
                <input type="text" value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sotib olish narxi</label>
                <input type="number" value={form.purchase_price}
                  onChange={e => setForm({ ...form, purchase_price: Number(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sotish narxi</label>
                <input type="number" value={form.sale_price}
                  onChange={e => setForm({ ...form, sale_price: Number(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
              {form.product_type === 'good' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Joriy qoldiq</label>
                    <input type="number" value={form.current_stock}
                      onChange={e => setForm({ ...form, current_stock: Number(e.target.value) })}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Minimal qoldiq</label>
                    <input type="number" value={form.min_stock}
                      onChange={e => setForm({ ...form, min_stock: Number(e.target.value) })}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategoriya</label>
                <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                  <option value="">— Tanlang —</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birlik</label>
                <select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                  {Object.entries(unitLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Yetkazib beruvchi</label>
                <select value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                  <option value="">— Tanlang —</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.company_name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor qilish</button>
              <button onClick={handleSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Price List Modal */}
      {showPriceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-xs">
            <h3 className="text-lg font-bold mb-4">📄 Narxlar ro'yxati (PDF)</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chegirma (%) — 0 bo'lsa chegirmasiz
              </label>
              <input type="number" min="0" max="100" value={discount}
                onChange={e => setDiscount(Number(e.target.value))}
                className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              {discount > 0 && (
                <p className="text-xs text-orange-600 mt-1">⚡ {discount}% chegirma bilan narxlar ko'rsatiladi</p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowPriceModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor</button>
              <button onClick={() => handlePriceList(discount)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                📥 Yuklab olish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kategoriyalar Modal */}
      {showCatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4">Kategoriyalar</h3>
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {categories.length === 0 ? (
                <p className="text-gray-400 text-sm">Kategoriyalar yo'q</p>
              ) : categories.map(c => (
                <div key={c.id} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                  <span className="font-medium text-sm">{c.name}</span>
                  {canFull && (
                    <button onClick={() => handleDeleteCategory(c.id)} className="text-red-500 hover:text-red-700 text-sm">🗑️</button>
                  )}
                </div>
              ))}
            </div>
            {canFull && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Yangi kategoriya</p>
                <input value={catForm.name} onChange={e => setCatForm({ ...catForm, name: e.target.value })}
                  placeholder="Kategoriya nomi"
                  className="w-full border rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                <input value={catForm.description} onChange={e => setCatForm({ ...catForm, description: e.target.value })}
                  placeholder="Tavsif (ixtiyoriy)"
                  className="w-full border rounded-lg px-3 py-2 mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowCatModal(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
                  <button onClick={handleAddCategory}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Qo'shish</button>
                </div>
              </div>
            )}
            {!canFull && (
              <div className="flex justify-end pt-4 border-t">
                <button onClick={() => setShowCatModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Kirim/Chiqim Modal */}
      {showStockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-1">Ombor operatsiyasi</h3>
            <p className="text-gray-500 text-sm mb-4">{showStockModal.name} — joriy qoldiq: <b>{showStockModal.current_stock}</b></p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operatsiya turi</label>
                <select value={stockForm.movement_type}
                  onChange={e => setStockForm({ ...stockForm, movement_type: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                  <option value="income">📥 Kirim</option>
                  <option value="outcome">📤 Chiqim</option>
                  <option value="adjustment">🔧 Tuzatish (yangi qoldiq)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Miqdor</label>
                <input type="number" min="1" value={stockForm.quantity}
                  onChange={e => setStockForm({ ...stockForm, quantity: Number(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <input value={stockForm.note}
                  onChange={e => setStockForm({ ...stockForm, note: e.target.value })}
                  placeholder="Masalan: Yetkazib beruvchidan keldi"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowStockModal(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor qilish</button>
              <button onClick={handleStockMovement} disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tarix Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-xl font-bold mb-1">Harakat tarixi</h3>
            <p className="text-gray-500 text-sm mb-4">{showHistoryModal.name}</p>
            <div className="max-h-80 overflow-y-auto">
              {movements.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Tarix yo'q</p>
              ) : movements.map(m => (
                <div key={m.id} className="flex justify-between items-center py-2 border-b last:border-0">
                  <div>
                    <span className={`font-medium text-sm ${movementLabels[m.movement_type]?.color}`}>
                      {movementLabels[m.movement_type]?.label}
                    </span>
                    {m.note && <p className="text-xs text-gray-400">{m.note}</p>}
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-lg">{m.quantity}</span>
                    <p className="text-xs text-gray-400">
                      {new Date(m.created_at).toLocaleDateString('uz-UZ')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowHistoryModal(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
